/**
 * Settings > Inputs: who last changed an input, and when.
 *
 * Every stored input already carries its writer and time on the row: the
 * database stamps them (tenant_rate_input.created_by / created_at,
 * container_physical_count.recorded_by / recorded_at, and updated_by /
 * updated_at on ro_goal_override and route_name_match), so the page never
 * supplies them. A stamp here is {at, by}: an ISO time and a user id.
 *
 * A user id becomes a name through public.report_edit_actor_names (migration
 * 0105): members of the tenant by name, else email; Meridian staff who are
 * not members as "Meridian staff". Until a name is known the label still
 * says what is true: "you" for the reader's own change, "Meridian" for a row
 * the service role wrote (no user id), "another user" otherwise.
 *
 * Plain .mjs so node:test covers the wording.
 */

/** The optimistic rows reportEdits.ts shows before a write returns. */
const PENDING = "pending";

/**
 * Who a stamp's user id is, in words.
 *
 * @param {string | null | undefined} id
 * @param {{ selfId?: string | null, names?: Map<string, string> | null }} [ctx]
 */
export function actorLabel(id, ctx = {}) {
  if (id === null || id === undefined || id === "") return "Meridian";
  if (id === PENDING || (ctx.selfId && id === ctx.selfId)) return "you";
  const name = ctx.names instanceof Map ? ctx.names.get(id) : null;
  return typeof name === "string" && name.trim() ? name.trim() : "another user";
}

/**
 * "Oct 1, 2026, 2:42 PM" in the reader's time zone (or `timeZone`, which the
 * tests pass so the answer does not depend on the machine).
 *
 * @param {string | null | undefined} iso
 * @param {{ timeZone?: string }} [opts]
 * @returns {string | null} null for a missing or unreadable time
 */
export function changedAtLabel(iso, opts = {}) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
  });
}

/**
 * The line under an input: "Oct 1, 2026, 2:42 PM by Zay". Null when the
 * input has no stored value (nothing was ever entered, or it was cleared).
 *
 * @param {{ at?: string | null, by?: string | null } | null | undefined} stamp
 * @param {{ selfId?: string | null, names?: Map<string, string> | null, timeZone?: string }} [ctx]
 */
export function lastChangedLabel(stamp, ctx = {}) {
  if (!stamp) return null;
  const who = actorLabel(stamp.by, ctx);
  const when = changedAtLabel(stamp.at, ctx);
  // A row this session has just written shows before its time comes back.
  return when ? `${when} by ${who}` : `just now by ${who}`;
}

/** The newest of several stamps (an input drawn from more than one row), or null. */
export function newestStamp(stamps) {
  let best = null;
  for (const s of stamps || []) {
    if (!s) continue;
    if (!best || String(s.at ?? "") > String(best.at ?? "")) best = s;
  }
  return best;
}

/** The user ids a set of stamps names, without blanks, the optimistic marker or repeats. */
export function actorIds(stamps) {
  const out = new Set();
  for (const s of stamps || []) {
    const id = s?.by;
    if (typeof id === "string" && id && id !== PENDING) out.add(id);
  }
  return [...out];
}

/** A tenant_rate_input row's stamp. */
export function rateStamp(row) {
  return row ? { at: row.created_at ?? null, by: row.created_by ?? null } : null;
}
